/**
 * The web's whole share of the affiliate pilot: notice `?aff=CODIGO` on the
 * way in, keep it, have the server count the visit, and hand the code over
 * once after the first sign-in.
 *
 * NOTHING HERE TALKS TO AFFITOR. No script, no request to any domain but
 * this app's own API: the server forwards the click to Affitor's public
 * endpoint and sends the lead and the sale with the API key (spec, decision
 * 5). That keeps this page at zero third-party scripts, which is the
 * condition storage.ts relies on for keeping the token in localStorage.
 *
 * NOTHING HERE MAY BREAK A SCREEN. Every entry point that touches the window
 * swallows: storage that throws in a private window, a request that fails
 * offline. The server is the truth and is idempotent; the local flags are a
 * courtesy that saves requests.
 *
 * `localStorage` for the code, and for longer than pending-invite.ts's day:
 * a visitor who reads the landing today and signs up next week from
 * the same browser is exactly the case this exists for. Bounded by
 * AFFILIATE_CODE_MAX_AGE_DAYS, matching the program's cookie window.
 * `sessionStorage` for "the visit was attempted", so a server that answered
 * null is asked again in a new tab and not on every render of this one.
 */

const CODE_KEY = "cuotas_affiliate_code";
const SENT_PREFIX = "cuotas_affiliate_sent:";
const VISIT_PREFIX = "cuotas_affiliate_visit:";
const CODE = /^[A-Za-z0-9_-]{1,64}$/;

/** How long a seen code counts, in days. The program's cookie window is 90 (spec §6). */
export const AFFILIATE_CODE_MAX_AGE_DAYS = 90;

/** What is kept between the visit and the sign-in. `clickId` is what the server got from Affitor, once it did. */
export type StoredCode = { code: string; clickId: string | null; at: string };

/** The `aff` parameter of a query string, or null when absent or not shaped like a code. */
export function readAffiliateCode(search: string): string | null {
  const value = new URLSearchParams(search).get("aff");
  return value !== null && CODE.test(value) ? value : null;
}

export function codeSentKey(userId: string): string {
  return `${SENT_PREFIX}${userId}`;
}

export function visitAttemptedKey(code: string): string {
  return `${VISIT_PREFIX}${code}`;
}

export function parseStoredCode(raw: string | null, now: Date): StoredCode | null {
  if (raw === null || raw === "") return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    const { code, clickId, at } = parsed as { code?: unknown; clickId?: unknown; at?: unknown };
    if (typeof code !== "string" || !CODE.test(code) || typeof at !== "string") return null;
    if (clickId !== null && typeof clickId !== "string") return null;
    const seen = new Date(at).getTime();
    if (Number.isNaN(seen)) return null;
    const ageDays = (now.getTime() - seen) / 86_400_000;
    return ageDays <= AFFILIATE_CODE_MAX_AGE_DAYS ? { code, clickId: clickId ?? null, at } : null;
  } catch {
    return null;
  }
}

/**
 * What to keep after a visit. The LAST code seen wins, like Affitor's own
 * last-click rule, and a new code starts over without a click id; the same
 * code keeps the click id it already earned; no code changes nothing.
 */
export function nextStoredCode(previous: StoredCode | null, seen: string | null, now: Date): StoredCode | null {
  if (seen === null) return previous;
  const at = now.toISOString();
  if (previous !== null && previous.code === seen) return { ...previous, at };
  return { code: seen, clickId: null, at };
}

type VisitDeps = {
  stored: StoredCode | null;
  attempted: (key: string) => boolean;
  markAttempted: (key: string) => void;
  register: (code: string) => Promise<{ clickId: string | null }>;
  save: (stored: StoredCode) => void;
};

/** Once per code per tab: ask the server to count the visit and keep the click id it got. */
export async function registerVisit(deps: VisitDeps): Promise<"registered" | "skipped"> {
  const { stored } = deps;
  if (stored === null || stored.clickId !== null) return "skipped";
  const key = visitAttemptedKey(stored.code);
  if (deps.attempted(key)) return "skipped";

  deps.markAttempted(key);
  try {
    const { clickId } = await deps.register(stored.code);
    if (clickId !== null) deps.save({ ...stored, clickId });
  } catch {
    // Offline, or the API not reachable yet. Sign-in registers the click
    // server-side if this never succeeds.
  }
  return "registered";
}

export function shouldSendCode(input: {
  status: string;
  userId: string | null;
  code: string | null;
  alreadySent: boolean;
}): boolean {
  return input.status === "signed-in" && input.userId !== null && input.code !== null && !input.alreadySent;
}

type SendDeps = {
  userId: string;
  status: string;
  stored: StoredCode | null;
  isSent: (key: string) => boolean;
  markSent: (key: string) => void;
  report: (input: { partnerCode: string; clickId: string | null; seenAt: string }) => Promise<void>;
};

/**
 * Flag first, request second: a failed request loses this courtesy flag's
 * value (the server never heard) rather than risking a second request, and
 * the server ignores repeats anyway.
 */
export async function sendAffiliateCode(deps: SendDeps): Promise<"sent" | "skipped"> {
  const key = codeSentKey(deps.userId);
  const code = deps.stored?.code ?? null;
  const decision = shouldSendCode({ status: deps.status, userId: deps.userId, code, alreadySent: deps.isSent(key) });
  if (!decision || deps.stored === null) return "skipped";

  deps.markSent(key);
  try {
    // `at` travels too: an account that already existed when the code was
    // seen is not a registration, and the server is the one that knows.
    await deps.report({ partnerCode: deps.stored.code, clickId: deps.stored.clickId, seenAt: deps.stored.at });
  } catch {
    // Offline, or a 401 on a token about to refresh. Nothing to show anybody.
  }
  return "sent";
}

// ---------------------------------------------------------------- the window

/**
 * Called once from main.tsx before React mounts, so the code is kept even if
 * the router rewrites the URL.
 */
export function captureAffiliateCode(): void {
  try {
    const next = nextStoredCode(readStoredAffiliateCode(), readAffiliateCode(window.location.search), new Date());
    if (next !== null) saveStoredAffiliateCode(next);
  } catch {
    // Private browsing, or storage full: the visit simply is not attributed.
  }
}

export function readStoredAffiliateCode(): StoredCode | null {
  try {
    return parseStoredCode(localStorage.getItem(CODE_KEY), new Date());
  } catch {
    return null;
  }
}

export function saveStoredAffiliateCode(stored: StoredCode): void {
  try {
    localStorage.setItem(CODE_KEY, JSON.stringify(stored));
  } catch {
    // Same as above.
  }
}

export function isCodeSent(key: string): boolean {
  try {
    return localStorage.getItem(key) !== null;
  } catch {
    return false;
  }
}

export function markCodeSent(key: string): void {
  try {
    localStorage.setItem(key, new Date().toISOString());
  } catch {
    // Private browsing: the server's idempotency covers the repeat.
  }
}

export function wasVisitAttempted(key: string): boolean {
  try {
    return sessionStorage.getItem(key) !== null;
  } catch {
    return false;
  }
}

export function markVisitAttempted(key: string): void {
  try {
    sessionStorage.setItem(key, "1");
  } catch {
    // Nothing to undo if storage cannot be touched.
  }
}
